import React, { useMemo } from "react";
import { useBar } from "../../../../context/BarContext";
import {
  getCurrentDateTime,
  filterShowsByDateAndTime,
  calculateTotalSales,
  calculatePercentageChange,
  calculateSalesBySlotLabels,
  unionSlotLabels,
  formatCurrency,
  formatPercentage,
  formatDateRange,
  venueWeekRangeYmd,
  venuePreviousWeekPartialRangeYmd,
  venueMonthRangeYmd,
  venuePreviousMonthPartialRangeYmd,
  venueYearRangeYmd,
  venuePreviousYearPartialRangeYmd,
} from "../../utils/financialsMath";
import { normalizeBarTimeZone } from "../../../../utils/civilDateTime";
import { DateTime } from "luxon";

const Overall = () => {
  const { selectedBar, barShows, showsLoading } = useBar();

  const timeSlotsRaw = selectedBar?.time_slots;

  const ymdToDateInZone = (ymd, tz) =>
    DateTime.fromISO(ymd, { zone: normalizeBarTimeZone(tz) }).toJSDate();

  // Calculate financial data for all periods
  const financialData = useMemo(() => {
    const tz = normalizeBarTimeZone(selectedBar?.time_zone);
    const ref = getCurrentDateTime();

    if (!barShows || barShows.length === 0) {
      const emptyBreakdown = {};

      const week = venueWeekRangeYmd(tz, ref);
      const prevWeek = venuePreviousWeekPartialRangeYmd(tz, ref);
      const month = venueMonthRangeYmd(tz, ref);
      const prevMonth = venuePreviousMonthPartialRangeYmd(tz, ref);
      const year = venueYearRangeYmd(tz, ref);
      const prevYear = venuePreviousYearPartialRangeYmd(tz, ref);

      return {
        thisWeek: {
          sales: 0,
          previousSales: 0,
          change: { percentage: 0, isPositive: true },
          dateRange: formatDateRange(
            ymdToDateInZone(week.startYmd, tz),
            ymdToDateInZone(week.endYmd, tz),
          ),
          previousDateRange: formatDateRange(
            ymdToDateInZone(prevWeek.startYmd, tz),
            ymdToDateInZone(prevWeek.endYmd, tz),
          ),
          timeSlotBreakdown: {
            thisPeriod: emptyBreakdown,
            previousPeriod: emptyBreakdown,
            comparison: emptyBreakdown,
          },
          slotLabels: [],
        },
        thisMonth: {
          sales: 0,
          previousSales: 0,
          change: { percentage: 0, isPositive: true },
          dateRange: formatDateRange(
            ymdToDateInZone(month.startYmd, tz),
            ymdToDateInZone(month.endYmd, tz),
          ),
          previousDateRange: formatDateRange(
            ymdToDateInZone(prevMonth.startYmd, tz),
            ymdToDateInZone(prevMonth.endYmd, tz),
          ),
          timeSlotBreakdown: {
            thisPeriod: emptyBreakdown,
            previousPeriod: emptyBreakdown,
            comparison: emptyBreakdown,
          },
          slotLabels: [],
        },
        thisYear: {
          sales: 0,
          previousSales: 0,
          change: { percentage: 0, isPositive: true },
          dateRange: formatDateRange(
            ymdToDateInZone(year.startYmd, tz),
            ymdToDateInZone(year.endYmd, tz),
          ),
          previousDateRange: formatDateRange(
            ymdToDateInZone(prevYear.startYmd, tz),
            ymdToDateInZone(prevYear.endYmd, tz),
          ),
          timeSlotBreakdown: {
            thisPeriod: emptyBreakdown,
            previousPeriod: emptyBreakdown,
            comparison: emptyBreakdown,
          },
          slotLabels: [],
        },
      };
    }

    const week = venueWeekRangeYmd(tz, ref);
    const prevWeek = venuePreviousWeekPartialRangeYmd(tz, ref);

    const thisWeekShows = filterShowsByDateAndTime(
      barShows,
      week.startYmd,
      week.endYmd,
      ref,
      tz,
    );
    const previousWeekShows = filterShowsByDateAndTime(
      barShows,
      prevWeek.startYmd,
      prevWeek.endYmd,
      prevWeek.referenceInstant,
      tz,
    );
    const thisWeekSales = calculateTotalSales(thisWeekShows);
    const previousWeekSales = calculateTotalSales(previousWeekShows);
    const weekChange = calculatePercentageChange(
      thisWeekSales,
      previousWeekSales
    );

    const formatBreakdownFromResult = (br) => {
      const formatted = {};
      Object.entries(br.breakdown || {}).forEach(([label, v]) => {
        formatted[label] = v.sales || 0;
      });
      return formatted;
    };

    const thisWeekBreakdown = calculateSalesBySlotLabels(
      thisWeekShows,
      timeSlotsRaw
    );
    const previousWeekBreakdown = calculateSalesBySlotLabels(
      previousWeekShows,
      timeSlotsRaw
    );

    const weekSlotLabels = unionSlotLabels(
      thisWeekBreakdown,
      previousWeekBreakdown
    );

    const weekComparisonBreakdown = {};
    weekSlotLabels.forEach((label) => {
      const thisSales = thisWeekBreakdown.breakdown[label]?.sales || 0;
      const previousSales = previousWeekBreakdown.breakdown[label]?.sales || 0;
      weekComparisonBreakdown[label] = calculatePercentageChange(
        thisSales,
        previousSales
      );
    });

    const month = venueMonthRangeYmd(tz, ref);
    const prevMonth = venuePreviousMonthPartialRangeYmd(tz, ref);

    const thisMonthShows = filterShowsByDateAndTime(
      barShows,
      month.startYmd,
      month.endYmd,
      ref,
      tz,
    );
    const previousMonthShows = filterShowsByDateAndTime(
      barShows,
      prevMonth.startYmd,
      prevMonth.endYmd,
      prevMonth.referenceInstant,
      tz,
    );
    const thisMonthSales = calculateTotalSales(thisMonthShows);
    const previousMonthSales = calculateTotalSales(previousMonthShows);
    const monthChange = calculatePercentageChange(
      thisMonthSales,
      previousMonthSales
    );

    const thisMonthBreakdown = calculateSalesBySlotLabels(
      thisMonthShows,
      timeSlotsRaw
    );
    const previousMonthBreakdown = calculateSalesBySlotLabels(
      previousMonthShows,
      timeSlotsRaw
    );

    const monthSlotLabels = unionSlotLabels(
      thisMonthBreakdown,
      previousMonthBreakdown
    );

    const monthComparisonBreakdown = {};
    monthSlotLabels.forEach((label) => {
      const thisSales = thisMonthBreakdown.breakdown[label]?.sales || 0;
      const previousSales = previousMonthBreakdown.breakdown[label]?.sales || 0;
      monthComparisonBreakdown[label] = calculatePercentageChange(
        thisSales,
        previousSales
      );
    });

    const year = venueYearRangeYmd(tz, ref);
    const prevYear = venuePreviousYearPartialRangeYmd(tz, ref);

    const thisYearShows = filterShowsByDateAndTime(
      barShows,
      year.startYmd,
      year.endYmd,
      ref,
      tz,
    );
    const previousYearShows = filterShowsByDateAndTime(
      barShows,
      prevYear.startYmd,
      prevYear.endYmd,
      prevYear.referenceInstant,
      tz,
    );
    const thisYearSales = calculateTotalSales(thisYearShows);
    const previousYearSales = calculateTotalSales(previousYearShows);
    const yearChange = calculatePercentageChange(
      thisYearSales,
      previousYearSales
    );

    const thisYearBreakdown = calculateSalesBySlotLabels(
      thisYearShows,
      timeSlotsRaw
    );
    const previousYearBreakdown = calculateSalesBySlotLabels(
      previousYearShows,
      timeSlotsRaw
    );

    const yearSlotLabels = unionSlotLabels(
      thisYearBreakdown,
      previousYearBreakdown
    );

    const yearComparisonBreakdown = {};
    yearSlotLabels.forEach((label) => {
      const thisSales = thisYearBreakdown.breakdown[label]?.sales || 0;
      const previousSales = previousYearBreakdown.breakdown[label]?.sales || 0;
      yearComparisonBreakdown[label] = calculatePercentageChange(
        thisSales,
        previousSales
      );
    });

    return {
      thisWeek: {
        sales: thisWeekSales,
        previousSales: previousWeekSales,
        change: weekChange,
        dateRange: formatDateRange(
          ymdToDateInZone(week.startYmd, tz),
          ymdToDateInZone(week.endYmd, tz),
        ),
        previousDateRange: formatDateRange(
          ymdToDateInZone(prevWeek.startYmd, tz),
          ymdToDateInZone(prevWeek.endYmd, tz),
        ),
        timeSlotBreakdown: {
          thisPeriod: formatBreakdownFromResult(thisWeekBreakdown),
          previousPeriod: formatBreakdownFromResult(previousWeekBreakdown),
          comparison: weekComparisonBreakdown,
        },
        slotLabels: weekSlotLabels,
      },
      thisMonth: {
        sales: thisMonthSales,
        previousSales: previousMonthSales,
        change: monthChange,
        dateRange: formatDateRange(
          ymdToDateInZone(month.startYmd, tz),
          ymdToDateInZone(month.endYmd, tz),
        ),
        previousDateRange: formatDateRange(
          ymdToDateInZone(prevMonth.startYmd, tz),
          ymdToDateInZone(prevMonth.endYmd, tz),
        ),
        timeSlotBreakdown: {
          thisPeriod: formatBreakdownFromResult(thisMonthBreakdown),
          previousPeriod: formatBreakdownFromResult(previousMonthBreakdown),
          comparison: monthComparisonBreakdown,
        },
        slotLabels: monthSlotLabels,
      },
      thisYear: {
        sales: thisYearSales,
        previousSales: previousYearSales,
        change: yearChange,
        dateRange: formatDateRange(
          ymdToDateInZone(year.startYmd, tz),
          ymdToDateInZone(year.endYmd, tz),
        ),
        previousDateRange: formatDateRange(
          ymdToDateInZone(prevYear.startYmd, tz),
          ymdToDateInZone(prevYear.endYmd, tz),
        ),
        timeSlotBreakdown: {
          thisPeriod: formatBreakdownFromResult(thisYearBreakdown),
          previousPeriod: formatBreakdownFromResult(previousYearBreakdown),
          comparison: yearComparisonBreakdown,
        },
        slotLabels: yearSlotLabels,
      },
    };
  }, [barShows, timeSlotsRaw, selectedBar?.time_zone]);

  if (showsLoading) {
    return (
      <div className="financials-overall">
        <div className="financial-grid">
          <div className="financial-card">
            <h3>This Week</h3>
            <div className="earnings-breakdown">
              <div className="time-slot">
                <span className="slot-label">Loading...</span>
                <span className="slot-amount">
                  <span className="spinner" />
                </span>
              </div>
            </div>
          </div>
          <div className="financial-card">
            <h3>This Month</h3>
            <div className="earnings-breakdown">
              <div className="time-slot">
                <span className="slot-label">Loading...</span>
                <span className="slot-amount">
                  <span className="spinner" />
                </span>
              </div>
            </div>
          </div>
          <div className="financial-card">
            <h3>This Year</h3>
            <div className="earnings-breakdown">
              <div className="time-slot">
                <span className="slot-label">Loading...</span>
                <span className="slot-amount">
                  <span className="spinner" />
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!selectedBar) {
    return (
      <div className="financials-overall">
        <div className="no-data-message">
          <p>Please select a bar to view financials.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="financials-overall">
      {/* Original Summary Cards */}
      <div className="financial-grid">
        {/* This Week Card */}
        <div className="financial-card">
          <span
            className="info-icon card-info-icon"
            data-tooltip="Shows total sales from Monday of this week through today. Only includes shows that have already ended. The change percentage compares this week's sales to the same period last week."
          >
            ℹ️
          </span>
          <h3>This Week</h3>
          <div className="date-range">{financialData.thisWeek.dateRange}</div>
          <div className="earnings-breakdown">
            <div className="time-slot">
              <span className="slot-label">Sales</span>
              <span className="slot-amount sales">
                {formatCurrency(financialData.thisWeek.sales)}
              </span>
            </div>
            <div className="time-slot total">
              <span className="slot-label">Change</span>
              <span
                className={`slot-amount ${
                  financialData.thisWeek.change.isPositive
                    ? "profit-positive"
                    : "profit-negative"
                }`}
              >
                {formatPercentage(
                  financialData.thisWeek.change.percentage,
                  financialData.thisWeek.change.isPositive
                )}
              </span>
            </div>
          </div>
        </div>

        {/* This Month Card */}
        <div className="financial-card">
          <span
            className="info-icon card-info-icon"
            data-tooltip="Shows total sales from the 1st of this month through today. Only includes shows that have already ended. The change percentage compares this month's sales to the same period last month."
          >
            ℹ️
          </span>
          <h3>This Month</h3>
          <div className="date-range">{financialData.thisMonth.dateRange}</div>
          <div className="earnings-breakdown">
            <div className="time-slot">
              <span className="slot-label">Sales</span>
              <span className="slot-amount sales">
                {formatCurrency(financialData.thisMonth.sales)}
              </span>
            </div>
            <div className="time-slot total">
              <span className="slot-label">Change</span>
              <span
                className={`slot-amount ${
                  financialData.thisMonth.change.isPositive
                    ? "profit-positive"
                    : "profit-negative"
                }`}
              >
                {formatPercentage(
                  financialData.thisMonth.change.percentage,
                  financialData.thisMonth.change.isPositive
                )}
              </span>
            </div>
          </div>
        </div>

        {/* This Year Card */}
        <div className="financial-card">
          <span
            className="info-icon card-info-icon"
            data-tooltip="Shows total sales from January 1st of this year through today. Only includes shows that have already ended. The change percentage compares this year's sales to the same period last year."
          >
            ℹ️
          </span>
          <h3>This Year</h3>
          <div className="date-range">{financialData.thisYear.dateRange}</div>
          <div className="earnings-breakdown">
            <div className="time-slot">
              <span className="slot-label">Sales</span>
              <span className="slot-amount sales">
                {formatCurrency(financialData.thisYear.sales)}
              </span>
            </div>
            <div className="time-slot total">
              <span className="slot-label">Change</span>
              <span
                className={`slot-amount ${
                  financialData.thisYear.change.isPositive
                    ? "profit-positive"
                    : "profit-negative"
                }`}
              >
                {formatPercentage(
                  financialData.thisYear.change.percentage,
                  financialData.thisYear.change.isPositive
                )}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Week Comparison Section */}
      <div className="comparison-section-box">
        <span
          className="info-icon section-info-icon"
          data-tooltip="Compares this week's sales to last week's sales, broken down by time slot. Previous Period shows last week's sales by time slot. This Period shows this week's sales by time slot. Comparison shows the percentage change for each time slot and overall. Shows are matched to time slots based on their start time."
        >
          ℹ️
        </span>
        <h2 className="comparison-section-title">Week Comparison</h2>
        <div className="financial-grid">
          {/* Previous Period Card */}
          <div className="financial-card">
            <h3>Previous Period</h3>
            <div className="date-range">
              {financialData.thisWeek.previousDateRange}
            </div>
            <div className="earnings-breakdown">
              {financialData.thisWeek.slotLabels.map((label) => {
                const sales =
                  financialData.thisWeek.timeSlotBreakdown.previousPeriod[
                    label
                  ] || 0;
                return (
                  <div key={label} className="time-slot">
                    <span className="slot-label">{label}:</span>
                    <span className="slot-amount sales">
                      {formatCurrency(sales)}
                    </span>
                  </div>
                );
              })}
              <div className="time-slot total">
                <span className="slot-label">Total:</span>
                <span className="slot-amount sales">
                  {formatCurrency(financialData.thisWeek.previousSales)}
                </span>
              </div>
            </div>
          </div>

          {/* This Period Card */}
          <div className="financial-card">
            <h3>This Period</h3>
            <div className="date-range">{financialData.thisWeek.dateRange}</div>
            <div className="earnings-breakdown">
              {financialData.thisWeek.slotLabels.map((label) => {
                const sales =
                  financialData.thisWeek.timeSlotBreakdown.thisPeriod[
                    label
                  ] || 0;
                return (
                  <div key={label} className="time-slot">
                    <span className="slot-label">{label}:</span>
                    <span className="slot-amount sales">
                      {formatCurrency(sales)}
                    </span>
                  </div>
                );
              })}
              <div className="time-slot total">
                <span className="slot-label">Total:</span>
                <span className="slot-amount sales">
                  {formatCurrency(financialData.thisWeek.sales)}
                </span>
              </div>
            </div>
          </div>

          {/* Comparison Card */}
          <div className="financial-card">
            <h3>Comparison</h3>
            <div className="date-range">
              {financialData.thisWeek.previousDateRange} vs{" "}
              {financialData.thisWeek.dateRange}
            </div>
            <div className="earnings-breakdown">
              {financialData.thisWeek.slotLabels.map((label) => {
                const comparison = financialData.thisWeek.timeSlotBreakdown
                  .comparison[label] || { percentage: 0, isPositive: true };
                return (
                  <div key={label} className="time-slot">
                    <span className="slot-label">{label}:</span>
                    <span
                      className={`slot-amount ${
                        comparison.isPositive
                          ? "profit-positive"
                          : "profit-negative"
                      }`}
                    >
                      {formatPercentage(
                        comparison.percentage,
                        comparison.isPositive
                      )}
                    </span>
                  </div>
                );
              })}
              <div className="time-slot total">
                <span className="slot-label">Overall:</span>
                <span
                  className={`slot-amount ${
                    financialData.thisWeek.change.isPositive
                      ? "profit-positive"
                      : "profit-negative"
                  }`}
                >
                  {formatPercentage(
                    financialData.thisWeek.change.percentage,
                    financialData.thisWeek.change.isPositive
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Month Comparison Section */}
      <div className="comparison-section-box">
        <span
          className="info-icon section-info-icon"
          data-tooltip="Compares this month's sales to last month's sales, broken down by time slot. Previous Period shows last month's sales by time slot. This Period shows this month's sales by time slot. Comparison shows the percentage change for each time slot and overall. Shows are matched to time slots based on their start time."
        >
          ℹ️
        </span>
        <h2 className="comparison-section-title">Month Comparison</h2>
        <div className="financial-grid">
          {/* Previous Period Card */}
          <div className="financial-card">
            <h3>Previous Period</h3>
            <div className="date-range">
              {financialData.thisMonth.previousDateRange}
            </div>
            <div className="earnings-breakdown">
              {financialData.thisMonth.slotLabels.map((label) => {
                const sales =
                  financialData.thisMonth.timeSlotBreakdown.previousPeriod[
                    label
                  ] || 0;
                return (
                  <div key={label} className="time-slot">
                    <span className="slot-label">{label}:</span>
                    <span className="slot-amount sales">
                      {formatCurrency(sales)}
                    </span>
                  </div>
                );
              })}
              <div className="time-slot total">
                <span className="slot-label">Total:</span>
                <span className="slot-amount sales">
                  {formatCurrency(financialData.thisMonth.previousSales)}
                </span>
              </div>
            </div>
          </div>

          {/* This Period Card */}
          <div className="financial-card">
            <h3>This Period</h3>
            <div className="date-range">
              {financialData.thisMonth.dateRange}
            </div>
            <div className="earnings-breakdown">
              {financialData.thisMonth.slotLabels.map((label) => {
                const sales =
                  financialData.thisMonth.timeSlotBreakdown.thisPeriod[
                    label
                  ] || 0;
                return (
                  <div key={label} className="time-slot">
                    <span className="slot-label">{label}:</span>
                    <span className="slot-amount sales">
                      {formatCurrency(sales)}
                    </span>
                  </div>
                );
              })}
              <div className="time-slot total">
                <span className="slot-label">Total:</span>
                <span className="slot-amount sales">
                  {formatCurrency(financialData.thisMonth.sales)}
                </span>
              </div>
            </div>
          </div>

          {/* Comparison Card */}
          <div className="financial-card">
            <h3>Comparison</h3>
            <div className="date-range">
              {financialData.thisMonth.previousDateRange} vs{" "}
              {financialData.thisMonth.dateRange}
            </div>
            <div className="earnings-breakdown">
              {financialData.thisMonth.slotLabels.map((label) => {
                const comparison = financialData.thisMonth.timeSlotBreakdown
                  .comparison[label] || { percentage: 0, isPositive: true };
                return (
                  <div key={label} className="time-slot">
                    <span className="slot-label">{label}:</span>
                    <span
                      className={`slot-amount ${
                        comparison.isPositive
                          ? "profit-positive"
                          : "profit-negative"
                      }`}
                    >
                      {formatPercentage(
                        comparison.percentage,
                        comparison.isPositive
                      )}
                    </span>
                  </div>
                );
              })}
              <div className="time-slot total">
                <span className="slot-label">Overall:</span>
                <span
                  className={`slot-amount ${
                    financialData.thisMonth.change.isPositive
                      ? "profit-positive"
                      : "profit-negative"
                  }`}
                >
                  {formatPercentage(
                    financialData.thisMonth.change.percentage,
                    financialData.thisMonth.change.isPositive
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Year Comparison Section */}
      <div className="comparison-section-box">
        <span
          className="info-icon section-info-icon"
          data-tooltip="Compares this year's sales to last year's sales, broken down by time slot. Previous Period shows last year's sales by time slot (from January 1st to the same date last year). This Period shows this year's sales by time slot (from January 1st to today). Comparison shows the percentage change for each time slot and overall. Shows are matched to time slots based on their start time."
        >
          ℹ️
        </span>
        <h2 className="comparison-section-title">Year Comparison</h2>
        <div className="financial-grid">
          {/* Previous Period Card */}
          <div className="financial-card">
            <h3>Previous Period</h3>
            <div className="date-range">
              {financialData.thisYear.previousDateRange}
            </div>
            <div className="earnings-breakdown">
              {financialData.thisYear.slotLabels.map((label) => {
                const sales =
                  financialData.thisYear.timeSlotBreakdown.previousPeriod[
                    label
                  ] || 0;
                return (
                  <div key={label} className="time-slot">
                    <span className="slot-label">{label}:</span>
                    <span className="slot-amount sales">
                      {formatCurrency(sales)}
                    </span>
                  </div>
                );
              })}
              <div className="time-slot total">
                <span className="slot-label">Total:</span>
                <span className="slot-amount sales">
                  {formatCurrency(financialData.thisYear.previousSales)}
                </span>
              </div>
            </div>
          </div>

          {/* This Period Card */}
          <div className="financial-card">
            <h3>This Period</h3>
            <div className="date-range">{financialData.thisYear.dateRange}</div>
            <div className="earnings-breakdown">
              {financialData.thisYear.slotLabels.map((label) => {
                const sales =
                  financialData.thisYear.timeSlotBreakdown.thisPeriod[
                    label
                  ] || 0;
                return (
                  <div key={label} className="time-slot">
                    <span className="slot-label">{label}:</span>
                    <span className="slot-amount sales">
                      {formatCurrency(sales)}
                    </span>
                  </div>
                );
              })}
              <div className="time-slot total">
                <span className="slot-label">Total:</span>
                <span className="slot-amount sales">
                  {formatCurrency(financialData.thisYear.sales)}
                </span>
              </div>
            </div>
          </div>

          {/* Comparison Card */}
          <div className="financial-card">
            <h3>Comparison</h3>
            <div className="date-range">
              {financialData.thisYear.previousDateRange} vs{" "}
              {financialData.thisYear.dateRange}
            </div>
            <div className="earnings-breakdown">
              {financialData.thisYear.slotLabels.map((label) => {
                const comparison = financialData.thisYear.timeSlotBreakdown
                  .comparison[label] || { percentage: 0, isPositive: true };
                return (
                  <div key={label} className="time-slot">
                    <span className="slot-label">{label}:</span>
                    <span
                      className={`slot-amount ${
                        comparison.isPositive
                          ? "profit-positive"
                          : "profit-negative"
                      }`}
                    >
                      {formatPercentage(
                        comparison.percentage,
                        comparison.isPositive
                      )}
                    </span>
                  </div>
                );
              })}
              <div className="time-slot total">
                <span className="slot-label">Overall:</span>
                <span
                  className={`slot-amount ${
                    financialData.thisYear.change.isPositive
                      ? "profit-positive"
                      : "profit-negative"
                  }`}
                >
                  {formatPercentage(
                    financialData.thisYear.change.percentage,
                    financialData.thisYear.change.isPositive
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Overall;
